import ArrowLeftIcon from "@/assets/icons/ArrowLeft";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PlateForm from "@/components/shared/PlateForm";
import PlateModal from "@/components/shared/PlateModal";
import { Center } from "@/components/ui/center";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useSessionsData } from "@/hooks/useSessions";
import { useVehicle } from "@/hooks/useVehicle";
import { useFocusEffect } from "@react-navigation/native";
import { useT } from "@transifex/react";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useState } from "react";
import { Pressable } from "react-native";

type ParamsType = { plate: string; state: string; nickname: string };
type DataFormType = { plate: string; nickname: string };

const EditPlate = () => {
  const { plate, state, nickname } = useLocalSearchParams<ParamsType>();
  const [isOpen, setIsOpen] = useState(false);
  const t = useT();

  const [formValues, setFormValues] = useState<DataFormType>({ plate, nickname });

  const { editPlate, removePlate, isPending, accountIsFetching, getPlateToUpdate } = useVehicle();
  const { data: sessionData, isFetching: isSessionLoading } = useSessionsData();

  const plateToEdit = getPlateToUpdate(plate, state);

  useFocusEffect(
    useCallback(() => {
      if (plateToEdit) {
        setFormValues({
          plate: plateToEdit.plate,
          nickname: plateToEdit.nickname,
        });
      }
    }, [plateToEdit]),
  );

  if (accountIsFetching || isSessionLoading || !sessionData) {
    return <EditPlateSkeleton />;
  }

  if (!plateToEdit) {
    return (
      <ProtectedRoute>
        <Screen>
          <Center>
            <VStack className="gap-[20px]">
              <Text className="font-[600] leading-[22px] text-[16px] text-typography-900">{t("Sorry, plate not found.")}</Text>
              <AppButton icon={ArrowLeftIcon} onPress={() => (router.canGoBack() ? router.back() : router.replace("/my-vehicles"))} title={t("Back")} />
            </VStack>
          </Center>
        </Screen>
      </ProtectedRoute>
    );
  }

  const onSubmit = (data: DataFormType) => {
    editPlate(data, plate, state);
    setFormValues(data);
  };

  const onRemovePlate = () => {
    removePlate(plate, state);
    setIsOpen(false);
  };

  return (
    <ProtectedRoute>
      <Screen>
        <PageTitle title={t("Edit Plate")} />
        {isOpen && <PlateModal isOpen={isOpen} type="remove" licensePlate={plate} onClose={() => setIsOpen(false)} onConfirm={onRemovePlate} />}
        <PlateForm defaultValues={formValues} onSubmit={onSubmit} loading={isPending} />
        <Pressable onPress={() => setIsOpen(true)}>
          <Text className="font-[700] leading-[20px] text-[14px] text-center text-typography-900 underline">{t("Remove Plate")}</Text>
        </Pressable>
      </Screen>
    </ProtectedRoute>
  );
};

const EditPlateSkeleton = () => {
  return (
    <Screen>
      <SkeletonText className="h-[24px] w-[150px] mx-auto" />
      <VStack className="gap-[20px] w-full">
        <SkeletonText className="h-[16px] w-[120px]" />
        <Skeleton className="h-[52px] w-full rounded-md" />
        <SkeletonText className="h-[16px] w-[120px]" />
        <Skeleton className="h-[52px] w-full rounded-md" />
        <HStack space="md" className="mt-4">
          <Skeleton className="h-12 w-1/2 rounded-md" />
          <Skeleton className="h-12 w-1/2 rounded-md" />
        </HStack>
        <SkeletonText className="h-[14px] w-[120px] mx-auto" />
      </VStack>
    </Screen>
  );
};

export default EditPlate;
